import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {HTH} from "../HTH";
import {CustomWiggle} from "gsap/dist/CustomWiggle";
import {gsap, Linear} from "gsap";
import {HTHGameConfig} from "../HTHGameConfig";

export class HTHHelper {

    public static symShakeWiggle1 = CustomWiggle.create("symShakeWiggle1", {
        wiggles: 50,
        amplitudeEase: Linear.easeNone,
        timingEase: Linear.easeNone,
    });
    public static symShakeWiggle2 = CustomWiggle.create("symShakeWiggle2", {
        wiggles: 40,
        amplitudeEase: Linear.easeNone,
        timingEase: Linear.easeNone,
    });

    public static get isIpad(): boolean {
        const resizeData: ScreenBounds = NolimitApplication.screenBounds;
        if (NolimitApplication.isLandscape) {
            return (resizeData.width >= 955 && resizeData.width <= 965);
        } else {
            return (resizeData.height >= 955 && resizeData.height <= 965);
        }
    }

    /**
     * Use this method to catch the position from the TransformWorld between two objects in two different containers/layers.
     * @param objToSet Uses the  PIXI.DisplayObject's container to remove the it's offset.
     * @param objToMimic PIXI.DisplayObject to copy the position from.
     * @param position — The world origin to calculate from.
     * @returns {PIXI.Point} Mimiced position.
     */
    public static copyLocalPosition(objToSet: PIXI.DisplayObject, objToMimic: PIXI.DisplayObject): void {
        objToSet.position.x = objToMimic.parent?.position.x + objToMimic.position.x;
        objToSet.position.y = objToMimic.parent?.position.y + objToMimic.position.y;
    }

    public static getBigWinLevel(): number {
        return 1;
    }

    public static isFreeSpin(mode: string): boolean {
        return mode.indexOf("FREESPIN") > -1;
    }

    public static isNormal(mode: string): boolean {
        return mode.indexOf("NORMAL") > -1;
    }

    public static isAvalanche(mode: string): boolean {
        return mode.indexOf("AVALANCHE") > -1;
    }

    public static checkForExtraSpin(win: number): boolean {
        if (HTH.model.data.isExtraSpin && HTH.model.mode.includes("FREESPIN") && HTH.model.extraSpinCost > 0) {
            return win >= HTH.model.extraSpinCost;
        }
        return true;
    }

    public static isBigWin(win: number, playedBetValue: number): boolean {

        let winRatio: number = MathHelper.ceilToDecimals((win / playedBetValue), 2);

        //to play level 4 & 5 again
        if (HTHGameConfig.isBigWinLevel5Played) {
            winRatio = MathHelper.ceilToDecimals(((win - HTHGameConfig.lastBigWinPlayedAmount) / playedBetValue), 2);
            for (let i = 3; i < HTH.model.bigWinType.length; i++) {
                if (winRatio >= HTH.model.bigWinType[i].ratio) {
                    return true;
                } else if (winRatio < HTH.model.bigWinType[i].ratio) {
                    break;
                }
            }
        }
        if (HTH.model.gameData?.betWayWins.length > 0 || HTH.model.isGodModeMaxWin) {
            for (let i = 0; i < HTH.model.bigWinType.length; i++) {
                if (winRatio >= HTH.model.bigWinType[i].ratio && (!HTHGameConfig.bigWinLevelPlayed[i] || HTH.model.brokeTheBank)) {
                    return true;
                } else if (winRatio < HTH.model.bigWinType[i].ratio) {
                    break;
                }
            }
        }
        return false;
    }

    public static copyArray(arr: string[][]): string[][] {
        const temp: string[][] = [];
        arr.forEach((elements: string[]) => {
            const newElements: string[] = [];
            elements.forEach((element: string) => {
                newElements.push(element);
            });
            temp.push(newElements);
        });
        return temp;
    }

    public static getBuyFeaturePrice(): number {
        let betLevel = HTH.apiPlugin.freeBets.hasFreeBets() ? HTH.apiPlugin.freeBets.getBet() : HTH.apiPlugin.betLevel.getLevel();
        let price: number = HTH.model.gameData?.playedBetValue || parseFloat(betLevel);
        if (HTH.model.data.wasFeatureBuy || HTH.model.data.boostedBet) {
            for (let index = 0; index < HTH.model.initData.featureBuyTimesBetValue.length; index++) {
                if (HTH.model.initData.featureBuyTimesBetValue[index].name === HTH.model.buyFeatureName) {
                    price *= HTH.model.initData.featureBuyTimesBetValue[index].price;
                    break;
                }
            }
        }
        return price;
    }

    public static isWinBelowStake(): boolean {
        return HTH.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && (HTH.model.data.accumulatedRoundWin <= this.getBuyFeaturePrice());
    }

    public static isWinBelowStakeCurrentBet(win: number): boolean {
        return HTH.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && (win <= this.getBuyFeaturePrice());
    }

    public static createSprite(spriteName?: string, x: number = 0, y: number = 0): PIXI.Sprite {
        const sprite: PIXI.Sprite = new PIXI.Sprite(spriteName ? HTH.assetLoader.getTextures(spriteName)[0] : PIXI.Texture.EMPTY);
        sprite.position.set(x, y);
        return sprite;
    }

    public static randomBetween(min: number, max: number): number {
        return Math.floor(Math.random() * (1 + max - min) + min);
    }

    public static sum(arr: number[]): number {
        return arr.reduce((a, b) => a + b, 0);
    }

    public static resetSpineItem(item: PIXI.spine.Spine): void {
        item.state.clearTracks();
        item.skeleton.setToSetupPose();
        //item.skeleton.setBonesToSetupPose();
        item.skeleton.setSlotsToSetupPose();
        item.updateTransform();
        item.state.setEmptyAnimation(0, 0);
    }

    public static playSpineWithTimeline(spine: PIXI.spine.Spine, trackIndex: number, animationName: string, animationAlpha: number = 1, mixDuration: number = 0, resetOnComplete: boolean = true): gsap.core.Timeline {
        const animation: PIXI.spine.core.Animation = spine.spineData.findAnimation(animationName);
        if (!animation) {
            throw new Error(`No animation named ${animationName}`);
        }

        spine.autoUpdate = false;

        const setAnimation = () => {
            const trackEntry: PIXI.spine.core.TrackEntry = spine.state.setAnimationWith(trackIndex, animation, false);
            trackEntry.alpha = animationAlpha;
            trackEntry.mixDuration = mixDuration;
        }

        const target = {
            playHead: 0,
            spine: spine,
            prevTime: 0
        }

        const updateFunc = () => {
            const now = target.playHead;
            spine.update(now - target.prevTime);
            target.prevTime = now;
        }

        const tl = gsap.timeline();
        tl.add(() => {
            target.prevTime = 0;
            setAnimation()
        });
        tl.add(gsap.fromTo(target, {playHead: 0}, {
            duration: animation.duration,
            playHead: animation.duration,
            onUpdate: updateFunc,
            ease: "none"
        }))
        if (resetOnComplete) {
            tl.add(() => {
                target.prevTime = 0;
                setAnimation();
            });
        }
        tl.add(gsap.to(target, {duration: 0.0001})); //Needed so gsap does not skip "setAnimation" during reverse play.
        tl.add(() => {
            target.playHead = animation.duration;
            updateFunc()
        })

        return tl;
    }

    public static spineAnimDuration(spine: PIXI.spine.Spine, animName: string): number {
        return spine.spineData.findAnimation(animName).duration;
    }
}